import { useState } from "react";
import { LineReveal, FadeUp } from "../components/Reveal";
import { ArrowUpRight, Check } from "lucide-react";
import { toast, Toaster } from "sonner";

const IMG = "https://images.unsplash.com/photo-1559136656-3db4bf6c35f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxjb2xsYWJvcmF0aXZlJTIwbWVldGluZyUyMG1vZGVybiUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3ODU2OTE3MTF8MA&ixlib=rb-4.1.0&q=85";

const focusOptions = [
  "1:1 Career Counseling",
  "Skill Workshops",
  "Graduate Program",
  "Executive Retainer",
  "I'm not sure yet",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", focus: focusOptions[0], message: "" });
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSent(true);
    toast.success("Thank you. We'll reply within two working days.");
  };

  return (
    <main data-testid="contact-page" className="pt-[112px]">
      <Toaster position="top-center" richColors />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="rule" /><span className="overline">The Enquiry</span>
          </div>
          <h1 className="font-serif font-light tracking-[-0.02em] leading-[0.94] text-[12vw] md:text-[8.5vw]">
            <LineReveal
              lines={[
                <>Write to us.</>,
                <>We <em className="italic text-[#C26D5C]">read</em> everything.</>,
              ]}
              stagger={0.12}
              delay={0.1}
            />
          </h1>
        </div>
      </section>

      <section className="pb-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6 md:gap-0 border border-[#1A1C1A]">
            {/* Left immersive */}
            <FadeUp className="col-span-12 md:col-span-5 relative min-h-[520px] overflow-hidden border-b md:border-b-0 md:border-r border-[#1A1C1A]">
              <img src={IMG} alt="Studio" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-[#1A1C1A]/45" />
              <div className="relative p-10 md:p-12 h-full flex flex-col justify-between text-[#F4F2F0]">
                <div>
                  <div className="overline mb-6">Studio Hours</div>
                  <p className="text-base leading-relaxed max-w-xs">
                    Mon — Fri, 09:00 to 18:00 ET.<br />
                    Consultations by appointment.
                  </p>
                </div>
                <div>
                  <div className="overline mb-4">Direct</div>
                  <p className="font-serif text-3xl md:text-4xl italic font-light leading-tight">
                    hello@careerfactors.co
                  </p>
                  <p className="mt-2 font-serif text-2xl md:text-3xl font-light">+1 (212) 555 0134</p>
                </div>
                <div>
                  <div className="overline mb-2">Studio</div>
                  <p className="text-sm leading-relaxed">17 Cedar Row, Suite 04<br />Brooklyn, New York 11201</p>
                </div>
              </div>
            </FadeUp>

            {/* Right form */}
            <FadeUp className="col-span-12 md:col-span-7 p-8 md:p-14 bg-[#F4F2F0]">
              {!sent ? (
                <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
                  <div className="overline text-[#1A1C1A]/70">Section 01 — Introduce yourself</div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="overline block mb-2">Full Name</label>
                      <input
                        data-testid="contact-name"
                        className="field"
                        placeholder="Jane Ellery"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="overline block mb-2">Email</label>
                      <input
                        data-testid="contact-email"
                        type="email"
                        className="field"
                        placeholder="jane@studio.co"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="overline text-[#1A1C1A]/70 mb-4">Section 02 — Area of focus</div>
                    <div className="flex flex-wrap gap-2">
                      {focusOptions.map((f) => (
                        <button
                          type="button"
                          key={f}
                          data-testid={`focus-${f.split(' ')[0].toLowerCase()}`}
                          onClick={() => setForm({ ...form, focus: f })}
                          className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
                            form.focus === f
                              ? "bg-[#1A1C1A] text-[#F4F2F0] border-[#1A1C1A]"
                              : "bg-transparent text-[#1A1C1A] border-[#1A1C1A] hover:bg-[#C26D5C] hover:border-[#C26D5C] hover:text-[#F4F2F0]"
                          }`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="overline text-[#1A1C1A]/70 mb-4">Section 03 — The context</div>
                    <label className="overline block mb-2">Tell us where you are.</label>
                    <textarea
                      data-testid="contact-message"
                      className="field min-h-[140px] resize-none"
                      placeholder="A paragraph is more useful than a bulleted list."
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                    />
                  </div>

                  <div className="pt-6 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-xs text-[#1A1C1A]/60 max-w-sm">
                      By writing, you consent to a considered reply within two working days. We keep every conversation confidential.
                    </p>
                    <button type="submit" data-testid="contact-submit" className="btn-cf">
                      Send Enquiry <ArrowUpRight size={14} />
                    </button>
                  </div>
                </form>
              ) : (
                <div data-testid="contact-success" className="min-h-[520px] flex flex-col justify-center items-start">
                  <div className="w-14 h-14 border border-[#1A1C1A] flex items-center justify-center mb-6">
                    <Check size={20} />
                  </div>
                  <h3 className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-tight max-w-lg">
                    Thank you, {form.name.split(' ')[0] || "friend"}. Your note is with us.
                  </h3>
                  <p className="mt-6 text-base leading-relaxed text-[#1A1C1A]/80 max-w-md">
                    We&rsquo;ll reply within two working days. In the meantime, our <em className="italic text-[#C26D5C]">Journal</em> is where we think out loud.
                  </p>
                  <button
                    onClick={() => { setSent(false); setForm({ name: "", email: "", focus: focusOptions[0], message: "" }); }}
                    data-testid="contact-reset"
                    className="btn-cf-ghost mt-10"
                  >
                    Write another
                  </button>
                </div>
              )}
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
